import React, { useState, useEffect } from 'react';
import WaiverListRow from "./WaiverListRow";
import moment from 'moment';
import _ from 'lodash';
import WaiverService from './WaiverService';
import FullPageLoadingSpinner from '../pages/components/fullPageLoadingSpinner';
import { Waiver } from '../types/waiver';

interface WaiverListProps {
  user: { accountId: string; id: string; };
  canEditWaivers: () => boolean;
  canViewAllWaivers: () => boolean;
  search: string;
}

const WAIVER_MAX_PUBLISHED_DAYS_OLD = 180;

const WaiverList: React.FC<WaiverListProps> = ({ user, canEditWaivers, canViewAllWaivers, search }) => {
  const [maxPublishedDaysOld, setMaxPublishedDaysOld] = useState(WAIVER_MAX_PUBLISHED_DAYS_OLD);
  const [waivers, setWaivers] = useState<Waiver[]>([]);
  const [filteredWaivers, setFilteredWaivers] = useState<Waiver[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    loadWaivers();
  }, [maxPublishedDaysOld, search]);

  const onChanges = (waivers: Waiver[]) => {
    if (search) {
      filterWaivers(waivers);
    }
  };

  const hasWaivers = () => {
    return !_.isEmpty(filteredWaivers);
  };

  const filterWaivers = (waivers: Waiver[]) => {
    if (!search) {
      setFilteredWaivers(waivers);
      onChanges(waivers);
    } else {
      setFilteredWaivers(_.filter(waivers, waiver => waiver.issuingAirlineCode === search));
    }
  };

  const loadWaivers = () => {
    setLoading(true);
    WaiverService.getWaivers(maxPublishedDaysOld, '').then(response => {
      setWaivers(response.data);
      filterWaivers(response.data);
      setLoading(false);
    });
  };

  const showMoreWaivers = () => {
    setMaxPublishedDaysOld(maxPublishedDaysOld + 90);
    setTimeout(() => {
      loadWaivers();
      window.scrollTo(0, window.document.body.scrollHeight);
    }, 0);
  };

  return (<>
    {loading && <FullPageLoadingSpinner />}
    <div className="row search-results" style={{ paddingTop: 0 }}>
      <div className="col-md-12">
        <div className="table-responsive waiver-results-table">
          <table className="table">
            <thead>
              <tr>
                <th className="waiver-issuing-airline col-md-1">Issuing Airline/Code</th>
                <th className="waiver-name col-md-2">Waiver Name</th>
                <th className="waiver-issue-date col-md-1">Issued/Updated:</th>
                <th className="waiver-travel-dates col-md-2">Impacted Travel Date(s)</th>
                <th className="waiver-airports col-md-2">Affected Airports/Countries</th>
                {canEditWaivers() && <th className="waiver-status col-md-1">Status</th>}
                <th className="waiver-last-modified-date col-md-1">Last Modified Date</th>
                {canViewAllWaivers() && <th className="col-md-1">Affected Trips</th>}
                <th className="col-md-1">Download PDF</th>
              </tr>
            </thead>
            <tbody>
              {filteredWaivers.map((waiver: Waiver) => (
                <WaiverListRow
                  key={waiver.id}
                  waiver={waiver}
                  canEditWaivers={canEditWaivers}
                  canViewAllWaivers={canViewAllWaivers}
                  user={user}
                  onWaiverChange={loadWaivers}
                />
              ))}
            </tbody>
          </table>
        </div>

        {!hasWaivers() && (
          <div className="waiver-info-box">
            There are no current waivers
            <br />
            {canEditWaivers() && (
              <span className="add-waiver-link waiver-header-link">
                <a href="/waivers/new/edit">Add a waiver</a>
              </span>
            )}
          </div>
        )}

        {(
          <div className="total-found">
            <button type="button" className="btn btn-default button-action" onClick={showMoreWaivers}>
              DISPLAY OLDER WAIVERS
            </button>
            <div className="button-note">
              Currently showing waivers for the last {maxPublishedDaysOld} days
            </div>
          </div>
        )}
      </div>
    </div>
  </>);
};

export default WaiverList;